import { Component, OnInit, OnDestroy, ComponentRef, HostListener, ViewChild, ViewContainerRef } from '@angular/core'
import { FormGroup, FormBuilder, Validators } from '@angular/forms'
import { IPublicClientApplication } from '@azure/msal-browser'
import { Store } from '@ngrx/store'
import { Subject } from 'rxjs'
import { CommonService } from 'src/app/services/common/common.service'
import { start_login, start_login_with_otp, start_sso_login } from 'src/app/store/action/login.action'
import { AppState } from 'src/app/store/state/app.state'
import { environment } from 'src/environments/environment'
import { ForgotPasswordComponent } from '../forgot-password/forgot-password.component'
import { ActivatedRoute } from '@angular/router'
import { LoginService } from 'src/app/services/login/login.service'
import { LoginDiscoveryResponseApiModel } from 'src/app/models/api.model'
import { AuthType } from 'src/app/models/model'
import { DynamicMsalService } from 'src/app/services/common/dynamic.msal.service'
import { LocalStorageService } from 'src/app/services/common/local-storage.service'

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit, OnDestroy {
  companyDomain!: string
  companyLogoURL: string
  otpBasedLoginForm!: FormGroup
  passwordBasedLoginForm!: FormGroup
  isIframe = false
  loginDisplay = false
  ssoLogin: boolean = false
  hideOtherLoginOptions = environment.hide_other_login_options
  private readonly _destroying$ = new Subject<void>()

  @ViewChild('targetContainer', { read: ViewContainerRef }) targetContainer: ViewContainerRef
  targetComponentRef: ComponentRef<ForgotPasswordComponent>
  private readonly PASSWORD = 'password'
  showOtpLogin = false
  showPasswordLogin = false
  showOtp = false
  isOtpTenant = true
  otpLength = 4

  otpConfig = {
    length: this.otpLength,
    allowNumbersOnly: true,
    inputClass: 'otp-input',
    containerClass: 'otp-input-container',
    isPasswordInput: false,
    autofocus: true,
    disableAutoFocus: false
  }

  resendCountdown = 30
  resendTimerRef: any

  maxResendAttempts = 3
  resendAttempts = 0

  canResendOtp = false
  resendLimitReached = false
  private msalInstance: IPublicClientApplication
  private ssoInProgress = false
  companySettings!: LoginDiscoveryResponseApiModel

  constructor(
    private formBuilder: FormBuilder,
    private appState: Store<AppState>,
    private cs: CommonService,
    private readonly route: ActivatedRoute,
    private readonly loginService: LoginService,
    private readonly dynamicMsalService: DynamicMsalService
  ) {}

  @HostListener('document:keydown.enter', ['$event'])
  onKeydownHandler(event: KeyboardEvent) {
    this.login()
  }

  ngOnInit(): void {
    this.companyDomain = this.route.snapshot.paramMap.get('companyDomain')!
    if (!this.companyDomain) {
      this.cs.showError('Company domain missing')
      this.cs.routeTo('/')
      return
    }
    LocalStorageService.set('companyDomain', this.companyDomain)
    const enable = this.route.snapshot.queryParamMap.get('enable')
    if (enable) {
      this.hideOtherLoginOptions = false
    }
    this.setCompanyLogoURL()
    this.buildOtpBasedLoginForm()
    this.buildPasswordBasedLoginForm()

    this.isIframe = window !== window.parent && !window.opener // Remove this line to use Angular Universal

    this.discoverLoginOptions()
  }

  private discoverLoginOptions() {
    if (this.companyDomain.toUpperCase() === 'RMS') {
      this.companySettings = {
        authType: AuthType.PASSWORD,
        clientId: '',
        tenantId: '',
        ssoProvider: '',
        companyGroupId: '',
        companyGroupCode: '',
        companyDomain: this.companyDomain,
        companyLogoUrl: ''
      }
    } else {
      let discoverySucceeded = false
      this.loginService.discoverLoginOptions(this.companyDomain).subscribe({
        next: (response: LoginDiscoveryResponseApiModel) => {
          if (!response) return
          discoverySucceeded = true
          this.companySettings = response
          this.showPasswordLogin = response.authType === AuthType.PASSWORD
          this.showOtpLogin = response.authType === AuthType.OTP
          this.ssoLogin = response.authType === AuthType.SSO

          if (response.authType === AuthType.PASSWORD) {
            this.buildPasswordBasedLoginForm()
          } else if (response.authType === AuthType.OTP) {
            this.buildOtpBasedLoginForm()
          } else if (response.authType === AuthType.SSO) {
            const authority = `https://login.microsoftonline.com/${this.companySettings.tenantId}`
            this.dynamicMsalService.init(response.clientId, authority)
          }
        },
        complete: () => {
          // LoginService.discoverLoginOptions swallows HTTP errors into a completed EMPTY
          // stream (showing a toast internally) rather than erroring — so a failed lookup
          // completes here having never emitted. Send the user back to retry instead of
          // leaving them on a blank page.
          if (!discoverySucceeded) {
            this.cs.routeTo('/')
          }
        }
      })
    }
  }

  isPasswordEnabled(): boolean {
    return this.companySettings?.authType === AuthType.PASSWORD
  }

  isOTPEnabled(): boolean {
    return this.companySettings?.authType === AuthType.OTP
  }

  isSSOEnabled(): boolean {
    return this.companySettings?.authType === AuthType.SSO
  }

  showPasswordOptions(): void {
    this.showOtpLogin = false
    this.showPasswordLogin = true
  }

  setCompanyLogoURL() {
    this.companyLogoURL = environment.gcp_storage_endpoint + 'company-logos/' + '.jpg'
  }

  buildPasswordBasedLoginForm() {
    this.passwordBasedLoginForm = this.formBuilder.group({
      email: ['', [Validators.required, Validators.email]],
      [this.PASSWORD]: ['', [Validators.required]]
    })
  }

  private buildOtpBasedLoginForm(): void {
    this.otpBasedLoginForm = this.formBuilder.group({
      email: ['', [Validators.required, Validators.email]],
      otp: ['', [Validators.required]]
    })
  }

  get f() {
    return this.passwordBasedLoginForm.controls
  }

  login() {
    if (this.passwordBasedLoginForm.invalid) {
      this.cs.showError('Enter valid email Id and Password')
      return
    }
    const formValue = { ...this.passwordBasedLoginForm.value }
    this.appState.dispatch(
      start_login({
        email: formValue.email,
        password: formValue.password
      })
    )
  }

  openTargetComponent(): void {
    if (!this.targetComponentRef) {
      this.targetComponentRef = this.targetContainer.createComponent(ForgotPasswordComponent)
    } else {
      this.targetComponentRef.instance.ngOnInit()
    }
  }

  handlePasswordChange(password: any) {
    this.passwordBasedLoginForm.controls[this.PASSWORD].setValue(password)
  }

  async loginWithSSO() {
    if (this.ssoInProgress) return
    if (!this.companySettings) {
      this.cs.showError('SSO not configured')
      return
    }

    this.ssoInProgress = true
    try {
      if (this.companySettings.ssoProvider === 'MICROSOFT') {
        if (!this.msalInstance) {
          const authority = `https://login.microsoftonline.com/${this.companySettings.tenantId}`
          this.msalInstance = await this.dynamicMsalService.init(this.companySettings.clientId, authority)
        }

        this.msalInstance
          .loginPopup({
            scopes: ['openid', 'profile', 'email']
          })
          .then((result: any) => {
            if (result?.idToken) {
              this.sendTokenToBE(result.idToken)
            }
          })
          .catch((error: any) => {
            console.error('MSAL login error:', error)
            return null
          })
      }
    } catch (err) {
      console.error('SSO login error:', err)
      this.cs.showError('SSO login failed')
    } finally {
      this.ssoInProgress = false
    }
  }

  sendTokenToBE(id_token: string) {
    this.appState.dispatch(
      start_sso_login({
        id_token,
        companyDomain: this.companyDomain
      })
    )
  }

  generateOtp() {
    if (!this.otpBasedLoginForm.get('email')?.valid) {
      this.cs.showError('Enter valid email Id')
      return
    }

    this.loginService.sendLoginOTP(this.otpBasedLoginForm.value.email).subscribe(() => {
      this.showOtp = true
      this.canResendOtp = false
      this.startResendTimer()
      this.cs.showSuccess('If an account exists for this email, an OTP will be sent.')
    })
  }

  onOtpChange(otp: string) {
    this.otpBasedLoginForm.get('otp')?.setValue(otp)
  }

  loginWithOtp(): void {
    const { email, otp } = this.otpBasedLoginForm.value

    if (!otp || otp.length !== this.otpLength) {
      return
    }

    this.appState.dispatch(
      start_login_with_otp({
        email,
        otp
      })
    )
  }

  resendOtp(): void {
    if (this.resendLimitReached || !this.canResendOtp) {
      return
    }

    this.resendAttempts++

    if (this.resendAttempts >= this.maxResendAttempts) {
      this.resendLimitReached = true
      this.canResendOtp = false
      return
    }

    this.generateOtp()
  }

  private startResendTimer(): void {
    this.canResendOtp = false
    this.resendCountdown = 30

    if (this.resendTimerRef) {
      clearInterval(this.resendTimerRef)
    }

    this.resendTimerRef = setInterval(() => {
      this.resendCountdown--

      if (this.resendCountdown === 0) {
        this.canResendOtp = true
        clearInterval(this.resendTimerRef)
      }
    }, 1000)
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined)
    this._destroying$.complete()
    if (this.resendTimerRef) {
      clearInterval(this.resendTimerRef)
    }
  }
}
